import { useState, useCallback } from 'react';
import type { StudioGreetingItem, StudioSongRequestItem, StudioNotificationItem, StudioModerationItem, SongRequestStatus } from '@/mocks/adminStudio';
import { studioGreetings, studioSongRequests, studioNotifications, studioModerationQueue } from '@/mocks/adminStudio';
import { SwalAlert } from '@/hooks/useSweetAlert';

type ToolsTab = 'saludos' | 'canciones' | 'notificaciones' | 'moderacion';

const tabDefs: { id: ToolsTab; label: string; icon: string; badge?: number }[] = [
  { id: 'saludos', label: 'Saludos', icon: 'ri-user-heart-line', badge: 3 },
  { id: 'canciones', label: 'Canciones', icon: 'ri-music-line', badge: 4 },
  { id: 'notificaciones', label: 'Notif.', icon: 'ri-notification-3-line', badge: 3 },
  { id: 'moderacion', label: 'Mod.', icon: 'ri-shield-line', badge: 2 },
];

const songStatusConfig: Record<SongRequestStatus, { label: string; dot: string; bg: string }> = {
  pendiente: { label: 'Pendiente', dot: 'bg-amber-500', bg: 'bg-amber-100 text-amber-700' },
  aceptada: { label: 'Aceptada', dot: 'bg-emerald-500', bg: 'bg-emerald-100 text-emerald-700' },
  reproduciendo: { label: 'Al aire', dot: 'bg-rose-500 animate-pulse', bg: 'bg-rose-100 text-rose-700' },
  completada: { label: 'Completada', dot: 'bg-foreground-400', bg: 'bg-foreground-100 text-foreground-600' },
  rechazada: { label: 'Rechazada', dot: 'bg-red-500', bg: 'bg-red-100 text-red-700' },
};

const notificationIcons: Record<string, { icon: string; color: string }> = {
  seguidor: { icon: 'ri-user-add-line', color: 'text-secondary-500' },
  vip: { icon: 'ri-vip-diamond-line', color: 'text-amber-500' },
  sistema: { icon: 'ri-settings-3-line', color: 'text-foreground-400' },
  alerta: { icon: 'ri-alert-line', color: 'text-red-500' },
  cancion: { icon: 'ri-music-line', color: 'text-sky-500' },
};

function formatTimeShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' });
}

export default function ToolsPanel() {
  const [activeTab, setActiveTab] = useState<ToolsTab>('saludos');
  const [greetings, setGreetings] = useState<StudioGreetingItem[]>(studioGreetings);
  const [songRequests, setSongRequests] = useState<StudioSongRequestItem[]>(studioSongRequests);
  const [notifications, setNotifications] = useState<StudioNotificationItem[]>(studioNotifications);
  const [moderation, setModeration] = useState<StudioModerationItem[]>(studioModerationQueue);

  const handleGreetingRead = useCallback((item: StudioGreetingItem) => {
    setGreetings((prev) => prev.map((g) => (g.id === item.id ? { ...g, read: true } : g)));
    SwalAlert.toast('Saludo leído', 'success');
  }, []);

  const handleGreetingRespond = useCallback((item: StudioGreetingItem) => {
    SwalAlert.toast(`Respondiendo a ${item.senderName}`, 'info');
  }, []);

  const handleSongAccept = useCallback((item: StudioSongRequestItem) => {
    setSongRequests((prev) => prev.map((s) => (s.id === item.id ? { ...s, status: 'aceptada' } : s)));
    SwalAlert.toast(`"${item.songName}" aceptada`, 'success');
  }, []);

  const handleSongReject = useCallback((item: StudioSongRequestItem) => {
    setSongRequests((prev) => prev.map((s) => (s.id === item.id ? { ...s, status: 'rechazada' } : s)));
    SwalAlert.toast(`"${item.songName}" rechazada`, 'warning');
  }, []);

  const handleBlockUser = useCallback(async (item: StudioModerationItem) => {
    const result = await SwalAlert.confirm('¿Bloquear usuario?', `Se bloqueará permanentemente a "${item.userName}".`);
    if (!result.isConfirmed) return;
    setModeration((prev) => prev.map((m) => (m.id === item.id ? { ...m, status: 'bloqueado' as const } : m)));
    SwalAlert.toast(`${item.userName} bloqueado`, 'success');
  }, []);

  const handleDismiss = useCallback(async (item: StudioModerationItem) => {
    const result = await SwalAlert.confirm('¿Descartar reporte?', `Se descartará el reporte de "${item.userName}".`);
    if (!result.isConfirmed) return;
    setModeration((prev) => prev.map((m) => (m.id === item.id ? { ...m, status: 'revisado' as const } : m)));
    SwalAlert.toast('Reporte descartado', 'info');
  }, []);

  return (
    <div className="h-full flex flex-col bg-background-50">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-background-200">
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
            <i className="ri-tools-line text-background-50 text-sm"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground-950">Herramientas</h3>
            <p className="text-[10px] text-foreground-400">Gestión rápida de cabina</p>
          </div>
        </div>
        {/* Tab pills */}
        <div className="flex items-center gap-1">
          {tabDefs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all relative ${
                activeTab === tab.id
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
              {tab.badge && tab.badge > 0 && (
                <span className={`px-1 py-0 rounded-full text-[8px] font-bold ${
                  activeTab === tab.id ? 'bg-background-50/30 text-background-50' : 'bg-background-200 text-foreground-500'
                }`}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto">
        {/* SALUDOS */}
        {activeTab === 'saludos' && (
          <div className="p-3 space-y-2">
            {greetings.length === 0 ? (
              <EmptySection message="No hay saludos pendientes" />
            ) : (
              greetings.map((item) => (
                <div key={item.id} className={`p-3 rounded-xl border ${item.read ? 'bg-background-50 border-background-200' : 'bg-emerald-50/60 border-emerald-200'}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-background-200 overflow-hidden shrink-0">
                      <img src={item.senderAvatar} alt={item.senderName} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-foreground-800 truncate">{item.senderName}</span>
                        <span className="text-[9px] text-foreground-400 shrink-0">{formatTimeShort(item.timestamp)}</span>
                      </div>
                      <p className="text-[10px] text-foreground-600 mt-0.5 line-clamp-2 leading-relaxed">{item.content}</p>
                      <div className="flex items-center gap-1 mt-1.5">
                        <button
                          onClick={() => handleGreetingRespond(item)}
                          className="px-2 py-1 rounded-md bg-primary-500 text-background-50 text-[9px] font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
                        >
                          <i className="ri-reply-line text-[8px] mr-0.5"></i>Responder
                        </button>
                        {!item.read && (
                          <button
                            onClick={() => handleGreetingRead(item)}
                            className="px-2 py-1 rounded-md bg-background-100 text-foreground-500 text-[9px] font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                          >
                            <i className="ri-check-line text-[8px] mr-0.5"></i>Leído
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* CANCIONES */}
        {activeTab === 'canciones' && (
          <div className="p-3 space-y-2">
            {songRequests.length === 0 ? (
              <EmptySection message="No hay canciones en cola" />
            ) : (
              songRequests.map((item) => {
                const sc = songStatusConfig[item.status];
                const isPending = item.status === 'pendiente';
                return (
                  <div key={item.id} className={`p-3 rounded-xl border ${item.status === 'reproduciendo' ? 'bg-rose-50/60 border-rose-200' : item.status === 'completada' || item.status === 'rechazada' ? 'bg-background-50 border-background-200 opacity-60' : 'bg-background-50 border-background-200'}`}>
                    <div className="flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                        <i className="ri-music-fill text-sky-500 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[11px] font-semibold text-foreground-800 truncate">{item.songName}</span>
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0 rounded text-[8px] font-semibold whitespace-nowrap ${sc.bg}`}>
                            <span className={`w-1 h-1 rounded-full ${sc.dot}`}></span>
                            {sc.label}
                          </span>
                        </div>
                        <p className="text-[10px] text-foreground-500 mt-0.5">{item.artist} · {item.requestedBy}</p>
                        {item.dedication && (
                          <p className="text-[9px] text-foreground-400 italic mt-0.5">"{item.dedication}"</p>
                        )}
                        {isPending && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <button
                              onClick={() => handleSongAccept(item)}
                              className="px-2 py-1 rounded-md bg-emerald-500 text-background-50 text-[9px] font-semibold hover:bg-emerald-600 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-check-line text-[8px] mr-0.5"></i>Aceptar
                            </button>
                            <button
                              onClick={() => handleSongReject(item)}
                              className="px-2 py-1 rounded-md bg-background-100 text-foreground-500 text-[9px] font-semibold hover:bg-red-50 hover:text-red-600 cursor-pointer transition-colors whitespace-nowrap"
                            >
                              <i className="ri-close-line text-[8px] mr-0.5"></i>Rechazar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* NOTIFICACIONES */}
        {activeTab === 'notificaciones' && (
          <div className="p-3 space-y-1.5">
            {notifications.length === 0 ? (
              <EmptySection message="No hay notificaciones" />
            ) : (
              notifications.map((item) => {
                const ni = notificationIcons[item.type] || notificationIcons.sistema;
                return (
                  <div key={item.id} className={`flex items-start gap-2.5 p-2.5 rounded-xl cursor-pointer transition-colors ${item.read ? 'hover:bg-background-50' : 'bg-background-100 hover:bg-background-200'}`}>
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${item.read ? 'bg-background-200' : 'bg-white'}`}>
                      <i className={`${ni.icon} ${ni.color} text-xs`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[10px] leading-relaxed ${item.read ? 'text-foreground-500' : 'text-foreground-800 font-medium'}`}>{item.message}</p>
                      <span className="text-[8px] text-foreground-400 mt-0.5 block">{formatTimeShort(item.timestamp)}</span>
                    </div>
                    {!item.read && (
                      <span className="w-2 h-2 rounded-full bg-primary-500 shrink-0 mt-1.5"></span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* MODERACIÓN */}
        {activeTab === 'moderacion' && (
          <div className="p-3 space-y-2">
            {moderation.length === 0 ? (
              <EmptySection message="Cola de moderación vacía" />
            ) : (
              moderation.map((item) => (
                <div key={item.id} className={`p-3 rounded-xl border ${item.status === 'bloqueado' ? 'bg-red-50/60 border-red-200' : item.status === 'revisado' ? 'bg-background-50 border-background-200 opacity-60' : 'bg-background-50 border-background-200'}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-7 h-7 rounded-full bg-background-200 overflow-hidden shrink-0">
                      <img src={item.avatar} alt={item.userName} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-foreground-800 truncate">{item.userName}</span>
                        <span className={`px-1.5 py-0 rounded text-[8px] font-semibold whitespace-nowrap ${
                          item.status === 'pendiente' ? 'bg-amber-100 text-amber-700' :
                          item.status === 'bloqueado' ? 'bg-red-100 text-red-700' :
                          'bg-foreground-100 text-foreground-600'
                        }`}>
                          {item.status === 'pendiente' ? 'Pendiente' : item.status === 'bloqueado' ? 'Bloqueado' : 'Revisado'}
                        </span>
                      </div>
                      <p className="text-[9px] text-foreground-500 mt-0.5">Motivo: {item.reason}</p>
                      <p className="text-[10px] text-foreground-400 italic mt-1 line-clamp-1 bg-background-100 rounded px-2 py-1">"{item.messagePreview}"</p>
                      {item.status === 'pendiente' && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <button
                            onClick={() => handleBlockUser(item)}
                            className="px-2 py-1 rounded-md bg-red-500 text-background-50 text-[9px] font-semibold hover:bg-red-600 cursor-pointer transition-colors whitespace-nowrap"
                          >
                            <i className="ri-forbid-2-line text-[8px] mr-0.5"></i>Bloquear
                          </button>
                          <button
                            onClick={() => handleDismiss(item)}
                            className="px-2 py-1 rounded-md bg-background-100 text-foreground-500 text-[9px] font-semibold hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                          >
                            <i className="ri-close-line text-[8px] mr-0.5"></i>Descartar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptySection({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-10 h-10 rounded-full bg-background-100 flex items-center justify-center mb-2">
        <i className="ri-inbox-line text-foreground-300 text-lg"></i>
      </div>
      <p className="text-[11px] text-foreground-400">{message}</p>
    </div>
  );
}